<template>
  <header id="renew2-header">
    <div class="menu_block renew2_wrapper">
      <!--ヘッダーロゴ左-->
      <div class="header-left">
        <h1 v-if="isYagiPrimeActive" id="yagiprime_member" class="h1">
          <router-link :to="{ path: '/', query: { to_top: 'true' } }">
            <img
              src="/static/renew3_img/yagiprime_img/logo.svg"
              alt="yagishブラウザでつくる履歴書・職務経歴書"
            />
          </router-link>
        </h1>

        <h1 v-else class="h1 flex">
          <router-link :to="{ path: '/', query: { to_top: 'true' } }">
            <img
              src="/static/renew2_img/yagish_logo_20230522.png"
              alt="yagishブラウザでつくる履歴書・職務経歴書"
            />
          </router-link>
        </h1>
      </div>

      <!--ヘッダー右: PC は「ロゴ＋メニュー＋ログインボタン」の1行-->
      <div class="header-right">
        <ul v-if="showServiceMenu" class="renew2_flex menu">
          <template v-if="isOemDisplayAllowed">
            <li
              v-for="item in pcMenuItems"
              :key="item.id"
              class="renew2_pc menu_list"
              :class="{ 'header-dropdown': item.children }"
            >
              <!--
                ドロップダウンはホバーとキーボードフォーカス（:focus-within）の
                両方で開く。静的 HTML 側と挙動を揃えるため JS の開閉状態は持たない。
              -->
              <template v-if="item.children">
                <button
                  type="button"
                  class="menu-item header-dropdown__trigger"
                  aria-haspopup="true"
                >
                  {{ item.label
                  }}<span
                    class="header-dropdown__caret"
                    aria-hidden="true"
                  ></span>
                </button>
                <ul class="header-dropdown__list">
                  <li v-for="child in item.children" :key="child.id">
                    <a :href="child.href" class="header-dropdown__link">{{
                      child.label
                    }}</a>
                  </li>
                </ul>
              </template>
              <a v-else :href="item.href" class="menu-item">{{ item.label }}</a>
            </li>
          </template>

          <li v-if="!isOemDisplayAllowed" style="flex: 1"></li>

          <!--ログインしてない場合-->
          <!--ログインフォームが会員登録も兼ねるようになったため導線は1つに統合-->
          <li v-if="!isAuthenticated" class="menu_list register-wrap">
            <a href="/login" class="register">ログイン・会員登録</a>
          </li>

          <li v-else class="menu_list">
            <a v-if="!isYagiPrimeActive" class="mypage-btn" href="/mypage"
              >マイページ</a
            >
            <a v-else class="mypage-btn" href="/mypage/yagiprime">
              マイページ
              <span class="header-count">{{ csCount }}</span>
            </a>
          </li>

          <li class="renew2_sp menu_list hamburgermenu-wrap">
            <slot name="sp-menu" />
          </li>
        </ul>

        <ul v-else class="renew2_flex menu">
          <li v-if="isAuthenticated" class="menu_list renew2_mypage">
            <a href="/mypage" class="on_mypage"
              ><span v-if="userDisplayName">{{ userDisplayName }} </span>[
              {{ userEmail }} ]
              <div id="email_for_googleads" style="display: none">
                {{ userEmail }}
              </div>
              <span class="renew2_font_small">さん</span></a
            >
          </li>
          <!--登録メールアドレス-->
        </ul>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router/composables'
import { useAuth } from '@/composables/useAuth'
import pcMenu from '@/assets/header/pc-menu.json'

type PcMenuLink = { id: string; label: string; href: string }
type PcMenuItem = {
  id: string
  label: string
  href?: string
  children?: PcMenuLink[]
}

const props = withDefaults(
  defineProps<{
    isYagiPrime?: boolean
    isYagiPrimeCancel?: boolean
    /** ヤギプライムのおすすめ求人 未読件数 */
    csCount?: number
    isOemDisplayAllowed?: boolean
  }>(),
  {
    isYagiPrime: false,
    isYagiPrimeCancel: false,
    csCount: 0,
    isOemDisplayAllowed: true
  }
)

const route = useRoute()
const { user, isAuthenticated } = useAuth()

const pcMenuItems = pcMenu.items as PcMenuItem[]

const isYagiPrimeActive = computed(
  () => props.isYagiPrime && !props.isYagiPrimeCancel
)

/** 障がい者向け書式ではサービスメニューを出さない（移設元の v-if を踏襲） */
const showServiceMenu = computed(
  () => route.params.formatId !== 'rirekisho_esf_challenged'
)

const userEmail = computed(() => user.value?.email ?? '')
const userDisplayName = computed(() => user.value?.displayName ?? '')
</script>

<style lang="scss" scoped>
#renew2-header .renew2_mypage {
  margin-right: 50px;
}

/*
 * ロゴ＋メニュー＋ログインボタンの1行構成なので縦中央で揃える。
 * body の border-top（緑帯）とメニューが接するため上に余白を足す。
 */
#renew2-header .menu_block {
  align-items: center;
  padding-top: 8px;
}

/* 「履歴書を作る」ドロップダウン */
$header-dropdown-offset: 6px;

/* リストの border 分ずれるので、隙間より少し大きく取る */
$header-dropdown-bridge: $header-dropdown-offset + 2px;

.header-dropdown {
  position: relative;
}

/* 他のメニュー項目と同じテキスト表現にする（枠・背景を持たない） */
.header-dropdown__trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font-weight: inherit;
  font-size: inherit;
  font-family: inherit;
  line-height: inherit;
  cursor: pointer;
}

.header-dropdown__caret {
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid currentcolor;
}

.header-dropdown__list {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 10;
  min-width: 220px;
  margin-top: $header-dropdown-offset;
  padding: 6px 0;
  background: #fff;
  border: 1px solid #dfdfdf;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgb(0 0 0 / 12%);
  visibility: hidden;
  opacity: 0;

  /* 閉じるときは fade が終わってから visibility を落とす（瞬間的な消滅を防ぐ） */
  transition:
    opacity 0.2s ease,
    visibility 0s linear 0.2s;
}

/*
 * トリガー（li）の下端とリストの間に margin 分の隙間があり、
 * そこを通過する一瞬だけ :hover が外れて開閉がちらついていた。
 * 隙間をリスト自身の当たり判定で埋めて、マウスが項目まで届くようにする。
 */
.header-dropdown__list::before {
  content: '';
  position: absolute;
  top: -$header-dropdown-bridge;
  left: 0;
  right: 0;
  height: $header-dropdown-bridge;
}

.header-dropdown:hover .header-dropdown__list,
.header-dropdown:focus-within .header-dropdown__list {
  visibility: visible;
  opacity: 1;
  transition:
    opacity 0.2s ease,
    visibility 0s;
}

.header-dropdown__link {
  display: block;
  padding: 8px 16px;
  color: #004d40;
  white-space: nowrap;
}

.header-dropdown__link:hover {
  opacity: 1;
  text-decoration: none;
  background: #f0f7ec;
  font-weight: bold;
}
</style>
